<script lang="ts">
  import type { IMessage } from "src/interfaces/Chat";
  import type { SharedAccountMessageHistoryData } from "../../interfaces/SharedAccountMessageHistory";
  import { fetchMessageHistory, getSharedAccountUserLabel } from "../../lib/sharedTrainerChat";
  import { parseDateWithTime } from "../../lib/parseDateWithTime";
  import { sharedAccountUserLabels } from "../../stores/sharedAccountUsers";
  import { ButtonComponent } from "ui";

  export let isOpen = false;
  export let message: IMessage | null = null;
  export let onClose: () => void = () => {};

  let isLoading = false;
  let errorMessage = "";
  let historyData: SharedAccountMessageHistoryData | null = null;
  let openedMessageId: number | null = null;

  const formatPayload = (payload?: string | null, isDeleted = false): string => {
    if (isDeleted) {
      return "Message deleted";
    }

    if (!payload || payload.trim() === "") {
      return "No text available";
    }

    return payload;
  };

  const loadHistory = async (): Promise<void> => {
    if (!message?.id) {
      return;
    }

    isLoading = true;
    errorMessage = "";

    try {
      historyData = await fetchMessageHistory(message.id);
    } catch (error: any) {
      historyData = null;
      errorMessage = error?.message || "Failed to load message history.";
    } finally {
      isLoading = false;
    }
  };

  const retry = async (): Promise<void> => {
    await loadHistory();
  };

  $: if (isOpen && message?.id && openedMessageId !== message.id) {
    openedMessageId = message.id;
    void loadHistory();
  }

  $: if (!isOpen) {
    openedMessageId = null;
    isLoading = false;
    errorMessage = "";
    historyData = null;
  }

  $: currentSenderLabel = historyData
    ? getSharedAccountUserLabel(
        $sharedAccountUserLabels,
        historyData.message.sharedAccountUserId,
        historyData.message.sharedAccountUserLabel
      )
    : getSharedAccountUserLabel(
        $sharedAccountUserLabels,
        message?.sharedAccountUserId,
        message?.sharedAccountUserLabel
      );

  $: sortedHistory = historyData?.history
    ? [...historyData.history].sort(
        (rowA, rowB) =>
          new Date(rowA.historyCreatedAt).getTime() - new Date(rowB.historyCreatedAt).getTime()
      )
    : [];

  $: currentMessageData = historyData?.message || message;
  $: originalPayload = sortedHistory.length
    ? formatPayload(sortedHistory[0].payload, false)
    : formatPayload(currentMessageData?.payload, Boolean(currentMessageData?.deletedAt));
</script>

{#if isOpen && message}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div class="fixed inset-0 z-[6] bg-black/50" on:click={onClose}></div>

  <div class="fixed inset-0 z-[7] flex items-center justify-center p-4">
    <div class="w-full max-w-[560px] max-h-[85vh] overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 text-slate-900 shadow-xl dark:border-zinc-600 dark:bg-zinc-800 dark:text-slate-100">
      <div class="mb-4 flex items-start justify-between gap-4">
        <div>
          <div class="text-lg font-semibold">Message history</div>
          {#if currentSenderLabel}
            <div class="text-sm text-slate-500 dark:text-slate-400">
              Sent by {currentSenderLabel}
            </div>
          {/if}
        </div>

        <ButtonComponent isOutline on:click={onClose}>Close</ButtonComponent>
      </div>

      {#if isLoading}
        <div class="py-8 text-sm text-slate-500 dark:text-slate-400">Loading history...</div>
      {:else if errorMessage}
        <div class="flex flex-col gap-3">
          <div class="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
            {errorMessage}
          </div>
          <div>
            <ButtonComponent isOutline on:click={retry}>Retry</ButtonComponent>
          </div>
        </div>
      {:else}
        <div class="flex flex-col gap-4">
          <div class="rounded-md border border-slate-200 p-3 dark:border-zinc-600">
            <div class="mb-1 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Original message
            </div>
            <div class="text-sm whitespace-pre-wrap">
              {originalPayload}
            </div>
            <div class="mt-2 text-xs text-slate-500 dark:text-slate-400">
              Created {parseDateWithTime(currentMessageData?.createdAt ?? message.createdAt)}
            </div>
          </div>

          <div>
            <div class="mb-2 text-sm font-semibold">Timeline</div>

            {#if sortedHistory.length}
              <div class="flex flex-col gap-3">
                {#each sortedHistory as historyRow, index (historyRow.id)}
                  <div
                    class={`rounded-md border p-3 ${
                      index === sortedHistory.length - 1
                        ? "border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/20"
                        : "border-slate-200 dark:border-zinc-600"
                    }`}
                  >
                    <div class="flex items-center justify-between gap-4">
                      <div class="text-sm font-medium">
                        {historyRow.changeType === "delete"
                          ? `Deleted by ${getSharedAccountUserLabel(
                              $sharedAccountUserLabels,
                              historyRow.changedBySharedAccountUserId,
                              historyRow.changedBySharedAccountUserLabel
                            ) || "Unknown"}`
                          : `Edited by ${getSharedAccountUserLabel(
                              $sharedAccountUserLabels,
                              historyRow.changedBySharedAccountUserId,
                              historyRow.changedBySharedAccountUserLabel
                            ) || "Unknown"}`}
                      </div>
                      <div class="text-xs text-slate-500 dark:text-slate-400">
                        {parseDateWithTime(historyRow.historyCreatedAt)}
                      </div>
                    </div>

                    <div class="mt-3 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {index === sortedHistory.length - 1 ? "Current state" : "Edited to"}
                    </div>
                    <div class="mt-1 whitespace-pre-wrap text-sm">
                      {#if historyRow.changeType === "delete"}
                        {formatPayload(undefined, true)}
                      {:else if sortedHistory[index + 1]}
                        {formatPayload(sortedHistory[index + 1].payload, false)}
                      {:else}
                        {formatPayload(
                          currentMessageData?.payload,
                          Boolean(currentMessageData?.deletedAt)
                        )}
                      {/if}
                    </div>
                  </div>
                {/each}
              </div>
            {:else}
              <div class="rounded-md border border-slate-200 p-3 text-sm text-slate-500 dark:border-zinc-600 dark:text-slate-400">
                No edit or delete history for this message.
              </div>
            {/if}
          </div>
        </div>
      {/if}
    </div>
  </div>
{/if}
